import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Twitter, Send } from "lucide-react";
import { SITE, NAV } from "../data/site";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're subscribed. Thank you for staying connected.");
      setEmail("");
    } catch (err) {
      toast.error("Sorry — something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[var(--tb-surface)] border-t border-[var(--tb-border)] mt-12">
      <div className="container-page py-14 md:py-20 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-2.5 mb-4">
            <span
              aria-hidden="true"
              className="grid place-items-center h-9 w-9 rounded-full bg-[var(--tb-primary)] text-white font-serif-display text-lg shadow-sm"
            >
              T
            </span>
            <span className="font-serif-display text-xl text-[var(--tb-text)]">Thrive Bridge</span>
          </div>
          <p className="text-[var(--tb-text-2)] text-pretty max-w-md">
            A UK Community Interest Company providing trauma-informed, culturally responsive mental health and
            wellbeing support to adults facing social and economic disadvantage.
          </p>
          <p className="mt-4 text-sm text-[var(--tb-muted)]">
            Registered Community Interest Company in England &amp; Wales.
          </p>
        </div>

        <div className="lg:col-span-2">
          <h4 className="font-serif-display text-base mb-3 text-[var(--tb-text)]">Explore</h4>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-[var(--tb-text-2)] hover:text-[var(--tb-primary)] transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/privacy" className="text-[var(--tb-text-2)] hover:text-[var(--tb-primary)]">Privacy Policy</Link>
            </li>
            <li>
              <Link to="/terms" className="text-[var(--tb-text-2)] hover:text-[var(--tb-primary)]">Terms of Use</Link>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="font-serif-display text-base mb-3 text-[var(--tb-text)]">Get in touch</h4>
          <ul className="space-y-2.5 text-sm text-[var(--tb-text-2)]">
            <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5" /> <a href={`mailto:${SITE.email}`} className="hover:text-[var(--tb-primary)]">{SITE.email}</a></li>
            <li className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5" /> <a href={`tel:${SITE.phoneRaw}`} className="hover:text-[var(--tb-primary)]">{SITE.phone}</a></li>
            <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5" /> {SITE.address}</li>
          </ul>
          <div className="mt-4 flex items-center gap-2">
            <a href={SITE.socials.facebook} aria-label="Facebook" className="h-9 w-9 grid place-items-center rounded-full border border-[var(--tb-border)] text-[var(--tb-text-2)] hover:text-[var(--tb-primary)] hover:border-[var(--tb-primary)] transition-colors"><Facebook className="h-4 w-4" /></a>
            <a href={SITE.socials.instagram} aria-label="Instagram" className="h-9 w-9 grid place-items-center rounded-full border border-[var(--tb-border)] text-[var(--tb-text-2)] hover:text-[var(--tb-primary)] hover:border-[var(--tb-primary)] transition-colors"><Instagram className="h-4 w-4" /></a>
            <a href={SITE.socials.linkedin} aria-label="LinkedIn" className="h-9 w-9 grid place-items-center rounded-full border border-[var(--tb-border)] text-[var(--tb-text-2)] hover:text-[var(--tb-primary)] hover:border-[var(--tb-primary)] transition-colors"><Linkedin className="h-4 w-4" /></a>
            <a href={SITE.socials.twitter} aria-label="Twitter/X" className="h-9 w-9 grid place-items-center rounded-full border border-[var(--tb-border)] text-[var(--tb-text-2)] hover:text-[var(--tb-primary)] hover:border-[var(--tb-primary)] transition-colors"><Twitter className="h-4 w-4" /></a>
          </div>
        </div>

        <div className="lg:col-span-3">
          <h4 className="font-serif-display text-base mb-3 text-[var(--tb-text)]">Stay connected</h4>
          <p className="text-sm text-[var(--tb-text-2)] mb-3">
            Occasional updates on workshops, news and ways to get involved.
          </p>
          <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
            <label htmlFor="newsletter-email" className="sr-only">Email address</label>
            <input
              id="newsletter-email"
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 rounded-full border border-[var(--tb-border)] bg-white px-4 py-2.5 text-sm focus:border-[var(--tb-primary)] focus:ring-2 focus:ring-[var(--tb-primary)]/20 outline-none"
            />
            <button
              type="submit"
              data-testid="newsletter-submit-button"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--tb-primary)] hover:bg-[var(--tb-primary-hover)] text-white px-5 py-2.5 text-sm font-semibold transition-all disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {loading ? "Sending…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-[var(--tb-border)]">
        <div className="container-page py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[var(--tb-muted)]">
          <p>© {new Date().getFullYear()} Thrive Bridge Mental Wellness CIC. All rights reserved.</p>
          <p className="text-pretty text-center md:text-right">
            If you are in immediate danger call 999 · Samaritans 116 123 (free, 24/7).
          </p>
        </div>
      </div>
    </footer>
  );
}
